import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface EmailSignature {
  id: string;
  signatureHtml: string;
  isActive: boolean;
  updatedAt: string;
}

export interface EffectiveSignature {
  id: string;
  signatureHtml: string;
  isTeamLevel: boolean;
  isAgentLevel?: boolean;
  updatedAt: string;
}

interface EmailSignaturesResponse {
  teamSignature: EmailSignature | null;
  agentSignature: EmailSignature | null;
  userSignature: EmailSignature | null;
  effectiveSignature: EffectiveSignature | null;
}

export function useEmailSignatures(agentId?: string | null) {
  const [data, setData] = useState<EmailSignaturesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSignatures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setData(null);
        return;
      }

      const body: Record<string, unknown> = {};
      if (agentId) {
        body.agentId = agentId;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-email-signatures`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch email signatures");
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(result.error);
      }

      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchSignatures();
  }, [fetchSignatures]);

  return {
    teamSignature: data?.teamSignature ?? null,
    agentSignature: data?.agentSignature ?? null,
    userSignature: data?.userSignature ?? null,
    effectiveSignature: data?.effectiveSignature ?? null,
    loading,
    error,
    refetch: fetchSignatures,
  };
}
